import {
  FONT_SIZE_ZOOM_FACTOR,
  MAX_EXPORT_SCENE_FONT_SIZE,
  MAX_ZOOM_FONT_SIZE,
  MAX_ZOOM_VALUE,
  MIN_EXPORT_SCENE_FONT_SIZE,
  MIN_ZOOM_FONT_SIZE,
  MIN_ZOOM_VALUE
} from "./constants";

/**
 * Function to get the adjusted font size for the RobotoText component, depending on the state of the floorplanner.
 *
 * @param {number} fontSize the normal font size.
 * @param {number} zoomData the zoom data in the floor planner.
 * @returns {number} the adjusted font size to be used for the text.
 */
export const getAdjustedFontSize = (
  fontSize: number,
  zoomData: number,
): number =>
  Math.max(
    Math.min(fontSize * (FONT_SIZE_ZOOM_FACTOR / zoomData), MAX_ZOOM_FONT_SIZE),
    MIN_ZOOM_FONT_SIZE,
  );

/**
 * Function to generate an appropriate font size for exporting the floor plan.
 * It adjusts the font size based on both the camera width and height, selecting
 * the dimension that best represents the overall room scale. Typically, the smaller
 * of the two dimensions is chosen to avoid extremely narrow or wide rooms from distorting the text.
 *
 * @param {number} cameraHeight The height of the camera used for scaling the font size.
 * @param {number} cameraWidth The width of the camera used for scaling the font size.
 * @returns {number} Adjusted font size for exporting based on the camera dimensions.
 */
export const getAdjustedFontSizeForExport = (
  cameraHeight: number,
  cameraWidth: number,
): number => {
  // We are using empirically determined camera dimension values for clamping.
  const MIN_CAMERA_HEIGHT = 10;
  const MIN_CAMERA_WIDTH = 5;
  const MAX_CAMERA_DIMENSION = 250;
  const CAMERA_DIMENSION_THRESHOLD = 80;

  // Use width only if it's at least CAMERA_DIMENSION_THRESHOLD units smaller than the height
  const cameraDimension =
    cameraHeight - cameraWidth >= CAMERA_DIMENSION_THRESHOLD
      ? cameraWidth
      : cameraHeight;

  // Use different min values depending on whether we're clamping the height or width
  const minCameraDimension =
    cameraDimension === cameraHeight ? MIN_CAMERA_HEIGHT : MIN_CAMERA_WIDTH;

  // Clamp the camera dimension to the defined range
  const clampedCameraDimension = Math.max(
    minCameraDimension,
    Math.min(cameraDimension, MAX_CAMERA_DIMENSION),
  );

  // Calculate the scaling factor based on the clamped camera dimension
  const scalingFactor =
    (clampedCameraDimension - minCameraDimension) /
    (MAX_CAMERA_DIMENSION - minCameraDimension);

  // Adjust the font size by interpolating between the min and max font sizes based on the camera dimension
  const adjustedFontSize =
    MIN_EXPORT_SCENE_FONT_SIZE +
    scalingFactor * (MAX_EXPORT_SCENE_FONT_SIZE - MIN_EXPORT_SCENE_FONT_SIZE);

  return adjustedFontSize;
};

/**
 * Calculates vertical offsets for text elements during export to maintain consistent spacing,
 * dynamically scaling based on the export camera dimensions.
 *
 * @param {number} exportCameraHeight - The height of the camera used for exporting.
 * @param {number} exportCameraWidth - The width of the camera used for exporting.
 * @param {boolean} hasTitle - Indicates whether the title is present or not.
 * @param {number} [baseLineSpacing=18] - The base spacing between lines in unscaled units.
 * @returns {number[]} Array of adjusted offset values for positioning text elements.
 */
 export const calculateVerticalTextShiftForExports = (
  exportCameraHeight: number,
  exportCameraWidth: number,
  hasTitle: boolean,
  baseLineSpacing: number = 1.2
): number[] => {
  // Shader-based scaling thresholds
  const baseSize = 50.0; // Reference size for scaling normalization
  const minSizeThreshold = 80.0; // Minimum size threshold
  const midSizeThreshold = 150.0; // Mid-size threshold
  const bigSizeThreshold = 300.0; // Large size threshold
  const hugeSizeThreshold = 450.0; // Huge size threshold

  // Scaling factors for each threshold (matching the shader)
  const smallScale = 0.8;
  const midScale = 1.5;
  const bigScale = 2.0;
  const hugeScale = 2.7;
  const maxScale = 3.0;

  // Determine the largest dimension, clamping to minSizeThreshold
  const largestDimension = Math.max(
    Math.max(exportCameraHeight, exportCameraWidth),
    minSizeThreshold
  );

  // Calculate scaling factor based on the shader logic
  let scalingFactor = baseSize / largestDimension;
  scalingFactor = Math.log2(1.0 + scalingFactor); // Logarithmic adjustment
  scalingFactor = Math.max(scalingFactor, smallScale); // Apply minimum scale

  if (largestDimension > hugeSizeThreshold) {
    scalingFactor = Math.max(scalingFactor, maxScale);
  } else if (largestDimension > bigSizeThreshold) {
    scalingFactor = Math.max(scalingFactor, hugeScale);
  } else if (largestDimension > midSizeThreshold) {
    scalingFactor = Math.max(scalingFactor, bigScale);
  } else if (largestDimension > minSizeThreshold) {
    scalingFactor = Math.max(scalingFactor, midScale);
  }

  // Scale line spacing dynamically based on scaling factor
  const adjustedLineSpacing = baseLineSpacing * scalingFactor;

  if (hasTitle) {
    // Calculate offsets for title, area, and perimeter
    const titleOffset = adjustedLineSpacing; // Title at the top
    const areaOffset = titleOffset - adjustedLineSpacing; // Area below the title
    const perimeterOffset = areaOffset - adjustedLineSpacing; // Perimeter below the area
    return [titleOffset, areaOffset, perimeterOffset];
  }

  // Offsets when the title is not present
  const areaOffset = adjustedLineSpacing / 2; // Center the area
  const perimeterOffset = areaOffset - adjustedLineSpacing; // Perimeter below the area
  return [0, areaOffset, perimeterOffset]; // Placeholder for title offset
};


/**
 * Calculates vertical offsets for text elements based on the zoom level, ensuring consistent spacing.
 *
 * @param {number} zoomData - The current zoom level of the camera in the scene.
 * @param {number} [lineSpacing=18] - The base spacing between lines, which scales dynamically with zoom.
 * @param {boolean} hasTitle - Indicates whether the title is present or not.
 * @returns {number[]} - Array containing the calculated offsets for the title (if present), area, and perimeter text elements.
 */
export const calculateVerticalTextShiftFromZoom = (
  zoomData: number,
  hasTitle: boolean,
  lineSpacing: number = 18,
): number[] => {
  // Scale spacing inversely proportional to zoom
  const zoomMultiplier = 1 / zoomData;

  const adjustedLineSpacing = lineSpacing * zoomMultiplier;

  if (hasTitle) {
    // Offsets when title is present
    const titleOffset = adjustedLineSpacing; // Title at the top
    const areaOffset = titleOffset - adjustedLineSpacing; // Area below title
    const perimeterOffset = areaOffset - adjustedLineSpacing; // Perimeter below area
    return [titleOffset, areaOffset, perimeterOffset];
  }

  // Offsets when title is not present
  const areaOffset = adjustedLineSpacing / 2; // Center the area
  const perimeterOffset = areaOffset - adjustedLineSpacing; // Perimeter below area
  return [0, areaOffset, perimeterOffset]; // Ensure a placeholder for titleOffset
};
/**
 * Function to interpolate the font size according to the zoom, it's boundaries and the bounds for the font size.
 *
 * @param {number} zoomLevel the current zoom level.
 * @returns {number} the interpolated font size.
 */
export const interpolateFontSize = (zoomLevel: number): number =>
  MAX_ZOOM_FONT_SIZE +
  ((zoomLevel - MIN_ZOOM_VALUE) / (MAX_ZOOM_VALUE - MIN_ZOOM_VALUE)) *
    (MIN_ZOOM_FONT_SIZE - MAX_ZOOM_FONT_SIZE);
